import './style.css';
import type { AdminView, PlayerView } from '@gardener/protocol';
import { type Component, createSignal, For, Match, onMount, Show, Switch } from 'solid-js';
import { Dynamic, render } from 'solid-js/web';
import { ApiError, call, claims, discord, token } from './api';
import { Codes } from './codes';
import { Creators } from './creators';
import { Maps } from './maps';
import { GROUPS, go, PAGES, type Page, route } from './nav';
import { Newcomers } from './newcomers';
import { openPalette, Palette } from './palette';
import { Players } from './players';
import { Population } from './population';
import { Reports } from './reports';
import { Shorts } from './shorts';
import { Skins } from './skins';
import { Tournament } from './tournament';
import { Dialogs, Icon, setToolbar, Toasts, toast } from './ui';

/**
 * The admin app (ADR-0150, redesigned by ADR-0154, admin.garden-wars.io): sign in as an account marked `admin` (by hand,
 * in the database), then its pages. A new page is a component here and a line in `nav.ts`' `PAGES`; the one open is in
 * the address (`#players/<id>`). ⌘K searches everything.
 */
const VIEWS: Record<Page, Component> = {
  players: Players,
  newcomers: Newcomers,
  reports: Reports,
  population: Population,
  skins: Skins,
  maps: Maps,
  codes: Codes,
  creators: Creators,
  tournament: Tournament,
  shorts: Shorts,
};

const mac = /Mac|iPhone|iPad/.test(navigator.platform);

function App() {
  const [me, setMe] = createSignal<AdminView | null>(null);
  const [checking, setChecking] = createSignal(true);
  const [error, setError] = createSignal('');
  /** The pages' list, folded out over a narrow window. */
  const [open, setOpen] = createSignal(false);

  /** Is this browser's token an admin's? Not: it is forgotten (signed out there too, when it is someone's). */
  async function check() {
    setChecking(true);
    try {
      setMe(token.get() ? await call<AdminView>('GET', '/api/admin/me') : null);
      setError('');
    } catch (err) {
      if (err instanceof ApiError && err.code === 'forbidden') await signOut('That account is not an admin.');
      else if (err instanceof ApiError && err.status === 401) token.set(null);
      else setError((err as Error).message);
    } finally {
      setChecking(false);
    }
  }
  const signedIn = (t: string) => {
    token.set(t);
    void check();
  };
  async function signOut(why = '') {
    await call('POST', '/api/players/me/signout').catch(() => {});
    token.set(null);
    setMe(null);
    setError(why);
  }

  onMount(() => {
    claims(signedIn, (err) => setError(err.message));
    void check();
  });

  return (
    <>
      <Switch>
        <Match when={checking() && !me()}>
          <div class="text-fg-muted flex h-dvh items-center justify-center text-sm">Loading…</div>
        </Match>
        <Match when={me()}>
          {(admin) => (
            <div
              data-app-shell
              class="bg-surface-root-bg max-lg:bg-surface-50-bg flex h-dvh w-full flex-col tracking-normal lg:flex-row"
            >
              <aside class="hidden h-full w-67 shrink-0 flex-col lg:flex">
                <Sidebar name={admin().name} signOut={() => void signOut()} />
              </aside>
              <div class="bg-surface-50-bg lg:edge-over flex min-h-0 min-w-0 flex-1 flex-col lg:my-2 lg:mr-2 lg:overflow-hidden lg:rounded-xl">
                <header class="edge-b flex min-h-12 shrink-0 flex-wrap items-center gap-x-3 gap-y-2 px-4 py-2">
                  <button
                    type="button"
                    class="text-fg-muted hover:bg-surface-100-hover-bg hover:text-fg-default -ml-1.5 flex size-7 cursor-pointer items-center justify-center rounded-md lg:hidden"
                    aria-label="Menu"
                    aria-expanded={open()}
                    onClick={() => setOpen(!open())}
                  >
                    <Icon name="menu" class="size-4" />
                  </button>
                  <div class="flex min-w-0 flex-1 items-center">
                    <Breadcrumbs />
                  </div>
                  <div ref={setToolbar} class="flex min-w-0 items-center gap-2 empty:hidden max-sm:w-full" />
                </header>
                <div class="relative flex min-h-0 w-full flex-1 flex-col overflow-y-auto">
                  <Dynamic component={VIEWS[route().page]} />
                </div>
              </div>
              <Show when={open()}>
                <div class="bg-surface-root-bg fixed inset-0 z-50 flex flex-col lg:hidden">
                  <button
                    type="button"
                    class="text-fg-muted hover:bg-surface-root-hover-bg hover:text-fg-default absolute top-4 right-3 z-10 flex size-7 cursor-pointer items-center justify-center rounded-md"
                    aria-label="Close"
                    onClick={() => setOpen(false)}
                  >
                    <Icon name="x" class="size-4" />
                  </button>
                  <Sidebar name={admin().name} signOut={() => void signOut()} navigated={() => setOpen(false)} />
                </div>
              </Show>
              <Palette signOut={() => void signOut()} />
            </div>
          )}
        </Match>
        <Match when={!me()}>
          <SignIn signedIn={signedIn} error={error()} setError={setError} />
        </Match>
      </Switch>
      <Dialogs />
      <Toasts />
    </>
  );
}

/** logdash's breadcrumbs (`Breadcrumbs.svelte`): the group, then the page open. */
function Breadcrumbs() {
  const page = () => PAGES[route().page];
  return (
    <nav aria-label="Breadcrumb" class="min-w-0">
      <ol class="flex min-w-0 items-center gap-2 text-[13px] font-medium">
        <li class="flex min-w-0 items-center gap-2">
          <span class="text-fg-muted truncate">{page().group}</span>
        </li>
        <li class="flex min-w-0 items-center gap-2">
          <span class="text-fg-disabled" aria-hidden="true">
            /
          </span>
          <span class="truncate" aria-current="page">
            {page().title}
          </span>
        </li>
      </ol>
    </nav>
  );
}

/** logdash's `SidebarMenuItem`: a row, lit while its page is open. */
const ITEM =
  'flex h-7.5 w-full min-w-0 items-center gap-1.5 rounded-lg px-2 text-sm font-medium select-none pointer-coarse:h-9';
const ITEM_IDLE = 'text-fg-tertiary hover:bg-surface-root-hover-bg hover:text-fg-default cursor-pointer';
const ITEM_ACTIVE = 'bg-surface-root-selected-bg text-fg-default';

/**
 * logdash's sidebar (`SidebarLayout`, `SidebarContent`): the account row on top (its menu signs out), Search where "All
 * domains" is, then each group's title over its pages, a footer under them.
 */
function Sidebar(props: { name: string; signOut: () => void; navigated?: () => void }) {
  const [menu, setMenu] = createSignal(false);
  return (
    <div class="flex min-h-0 flex-1 flex-col">
      <div class="relative flex h-14 shrink-0 items-center px-3 pt-2">
        <button
          type="button"
          class="group hover:bg-surface-root-hover-bg flex h-7.5 max-w-full min-w-0 cursor-pointer items-center gap-2 rounded-lg px-1.5 text-left"
          aria-expanded={menu()}
          onClick={() => setMenu(!menu())}
        >
          <span class="bg-surface-200-bg flex size-5.5 shrink-0 items-center justify-center overflow-hidden rounded-md">
            <Icon name="leaf" class="text-fg-secondary size-3.5" />
          </span>
          <span class="truncate text-sm font-[550]">Garden Wars</span>
          <Icon name="chevron" class="text-fg-muted group-hover:text-fg-secondary transition-ink size-3.5 shrink-0" />
        </button>
        <Show when={menu()}>
          <div class="fixed inset-0 z-40" aria-hidden="true" onClick={() => setMenu(false)} />
          <ul class="ld-menu bg-surface-elevated-bg border-surface-elevated-border absolute top-full left-3 z-50 w-56 rounded-xl border">
            <li class="text-fg-muted truncate px-3 pt-1 pb-1.5 text-xs">{props.name}</li>
            <li>
              <button
                type="button"
                class="flex w-full items-center gap-3 rounded-lg"
                onClick={() => {
                  setMenu(false);
                  // Scripts' token (tools/shorts' SHORTS_ADMIN_TOKEN): this sign-in, dead once it signs out.
                  navigator.clipboard.writeText(token.get() ?? '').then(
                    () => toast('Token copied'),
                    () => toast('Could not copy', 'error'),
                  );
                }}
              >
                <Icon name="copy" class="inline size-4" />
                Copy token
              </button>
            </li>
            <li>
              <button type="button" class="flex w-full items-center gap-3 rounded-lg" onClick={props.signOut}>
                <Icon name="logout" class="inline size-4" />
                Sign out
              </button>
            </li>
          </ul>
        </Show>
      </div>
      <div class="flex min-h-0 flex-1 flex-col gap-4 px-3 pt-1">
        <button
          type="button"
          class={`${ITEM} ${ITEM_IDLE}`}
          onClick={() => {
            props.navigated?.();
            openPalette();
          }}
        >
          <Icon name="search" class="size-4 shrink-0" />
          <span class="truncate">Search</span>
          <span class="ld-kbd ml-auto" data-size="sm">
            {mac ? '⌘K' : 'Ctrl K'}
          </span>
        </button>
        <nav
          class="flex min-h-0 flex-col gap-0.5 overflow-y-auto pb-6 [mask-image:linear-gradient(to_bottom,black_calc(100%_-_1.5rem),transparent)]"
          aria-label="Pages"
        >
          <For each={GROUPS}>
            {([group, pages]) => (
              <>
                <div class="text-fg-tertiary flex h-7.5 shrink-0 items-center px-2 text-[13px] font-medium not-first:mt-3">
                  {group}
                </div>
                <For each={pages}>
                  {(p) => (
                    <button
                      type="button"
                      class={`${ITEM} ${route().page === p ? ITEM_ACTIVE : ITEM_IDLE}`}
                      aria-current={route().page === p ? 'page' : undefined}
                      onClick={() => {
                        props.navigated?.();
                        go(p);
                      }}
                    >
                      <Icon name={PAGES[p].icon} class="size-4 shrink-0" />
                      <span class="truncate">{PAGES[p].title}</span>
                    </button>
                  )}
                </For>
              </>
            )}
          </For>
        </nav>
      </div>
      <div class="flex h-12 shrink-0 items-center gap-1 px-3">
        <span class="text-fg-tertiary truncate px-1 text-[13px] font-medium">{props.name}</span>
      </div>
    </div>
  );
}

/**
 * A login and password (`POST /api/players/signin`), or Discord: logdash's auth page (`AuthPage`, `AuthForm`), the
 * logotype in the corner and the form's card in the middle of the panel.
 */
function SignIn(props: { signedIn: (token: string) => void; error: string; setError: (e: string) => void }) {
  const [login, setLogin] = createSignal('');
  const [password, setPassword] = createSignal('');
  const [busy, setBusy] = createSignal(false);
  async function submit(e: SubmitEvent) {
    e.preventDefault();
    setBusy(true);
    try {
      const body = { login: login(), password: password() };
      props.signedIn((await call<{ player: PlayerView; token: string }>('POST', '/api/players/signin', body)).token);
    } catch (err) {
      props.setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <div class="flex min-h-dvh w-full overflow-hidden">
      <div class="bg-surface-100-bg relative flex w-full flex-col p-8">
        <div class="absolute top-8 left-8">
          <span class="inline-flex items-center gap-2 text-[24px] leading-none" role="img" aria-label="Garden Wars">
            <span class="bg-surface-200-bg flex size-[1.4em] shrink-0 items-center justify-center rounded-[0.3em]">
              <Icon name="leaf" class="text-fg-secondary size-[0.9em]" />
            </span>
            <span class="font-semibold tracking-[-0.03em]">Garden Wars</span>
          </span>
        </div>
        <div class="flex flex-1 items-center justify-center">
          <form class="ld-card-box w-md max-w-full" onSubmit={submit}>
            <div class="ld-card-body items-center p-6 text-center">
              <h2 class="ld-card-title mb-2 text-3xl font-bold">Welcome back</h2>
              <p class="text-fg-tertiary mb-6 text-balance">Only accounts marked admin in the database get in.</p>
              <div class="flex w-full flex-col gap-4 text-left">
                <div class="flex flex-col gap-1.5">
                  <label class="ld-label text-sm font-medium" for="signin-login">
                    Login
                  </label>
                  <input
                    id="signin-login"
                    class="ld-text-input w-full"
                    autocomplete="username"
                    value={login()}
                    onInput={(e) => setLogin(e.currentTarget.value)}
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="ld-label text-sm font-medium" for="signin-password">
                    Password
                  </label>
                  <input
                    id="signin-password"
                    class="ld-text-input w-full"
                    type="password"
                    autocomplete="current-password"
                    value={password()}
                    onInput={(e) => setPassword(e.currentTarget.value)}
                  />
                </div>
              </div>
              <button
                type="submit"
                class="ld-button mt-2"
                data-variant="primary"
                data-block
                disabled={busy()}
                aria-busy={busy() || undefined}
              >
                <Show when={busy()} fallback="Sign in">
                  <span class="ld-button-content">Sign in</span>
                  <span class="ld-button-spinner">
                    <span class="ld-spinner" data-size="sm" aria-hidden="true" />
                  </span>
                </Show>
              </button>
              <div class="ld-divider text-fg-muted my-1 text-xs">or</div>
              <button
                type="button"
                class="ld-button gap-2"
                data-block
                onClick={() => discord().catch((err: Error) => props.setError(err.message))}
              >
                <svg class="size-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z" />
                </svg>
                Sign in with Discord
              </button>
              <Show when={props.error}>
                <p class="text-error mt-4 text-sm" role="alert">
                  {props.error}
                </p>
              </Show>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

render(() => <App />, document.getElementById('app') as HTMLElement);
